import { useGetOrganisationDeliveryLocations } from "@fero/hooks";
import { FormControl, RadioGroup, Stack } from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import type { OrganisationDeliveryLocation } from "adl-gen/ferovinum/app/db";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import type { FormikForm } from "components/types/formik-types";
import { useCallback, useContext, useMemo } from "react";
import { isAdmin } from "utils/user-utils";
import { LoggedInContext } from "../../../../../../app/app";
import type { RepurchaseRequest } from "../../organisation-repurchase-stock/organisation-repurchase-stock-page";
import type { DeliveryType } from "../organisation-repurchase-delivery-page";
import type { DeliveryFormValues } from "../organisation-repurchase-delivery-page-view";
import { CustomerDeliveryOption } from "./customer-delivery-option";
import { RadioDeliveryOption } from "./radio-delivery-option";
import { SavedDeliveryOption } from "./saved-delivery-option";
import { WarehouseDeliveryOption } from "./warehouse-delivery-option";

export const AvailableDeliveryOptions = ({
  repurchaseRequest,
  storageLocation,
  internalTransferLocations,
  form,
}: {
  repurchaseRequest: RepurchaseRequest;
  storageLocation: StorageLocationView;
  internalTransferLocations: StorageLocationView[];
  form: FormikForm<DeliveryFormValues>;
}) => {
  const loggedInUserContext = useContext(LoggedInContext);
  const userProfile = loggedInUserContext.loginState?.user?.profile;
  const userIsAdmin = !!(userProfile && isAdmin(userProfile));
  const organisationDeliveryLocations = useGetOrganisationDeliveryLocations();

  const availableDeliveryOptions = useMemo(
    () =>
      getAvailableDeliveryOptions(
        repurchaseRequest,
        storageLocation,
        organisationDeliveryLocations.data ?? [],
        userIsAdmin,
      ),
    [
      organisationDeliveryLocations.data,
      repurchaseRequest,
      storageLocation,
      userIsAdmin,
    ],
  );

  const onChange = useCallback(
    (_e, value) => form.setFieldValue("selectedType", value),
    [form],
  );

  const isSelected = useCallback(
    (option: DeliveryType) => form.values.selectedType === option,
    [form.values.selectedType],
  );

  const { canSelectSiteCode, siteCodeIsRequired } = useMemo(
    () => ({
      canSelectSiteCode: internalTransferLocations.length > 1,
      siteCodeIsRequired: internalTransferLocations.length >= 1,
    }),
    [internalTransferLocations.length],
  );

  return (
    <BaseWizardCard
      title={"Delivery Options"}
      inputElement={
        <Stack spacing={2} width={"100%"}>
          <FormControl>
            <RadioGroup
              value={form.values.selectedType}
              onChange={onChange}
              name="selectedType"
            >
              {availableDeliveryOptions.warehouse && (
                <RadioDeliveryOption
                  selected={isSelected("warehouse")}
                  value="warehouse"
                  label="Keep stock in the warehouse"
                >
                  <WarehouseDeliveryOption
                    form={form}
                    storageLocation={storageLocation}
                    internalTransferLocations={internalTransferLocations}
                    canSelectSiteCode={canSelectSiteCode}
                    siteCodeIsRequired={siteCodeIsRequired}
                  />
                </RadioDeliveryOption>
              )}
              {availableDeliveryOptions.savedDeliveryLocation && (
                <RadioDeliveryOption
                  selected={isSelected("savedDeliveryLocation")}
                  value="savedDeliveryLocation"
                  label="Deliver to a saved location"
                >
                  <SavedDeliveryOption
                    form={form}
                    storageLocation={storageLocation}
                  />
                </RadioDeliveryOption>
              )}
              {availableDeliveryOptions.customerLocation && (
                <RadioDeliveryOption
                  selected={isSelected("customerLocation")}
                  value="customerLocation"
                  label="Specify an address to deliver to"
                >
                  <CustomerDeliveryOption
                    form={form}
                    storageLocation={storageLocation}
                  />
                </RadioDeliveryOption>
              )}
            </RadioGroup>
          </FormControl>
        </Stack>
      }
    />
  );
};

function getAvailableDeliveryOptions(
  repurchaseRequest: RepurchaseRequest,
  storageLocationView: StorageLocationView,
  organisationDeliveryLocations: WithDbId<OrganisationDeliveryLocation>[],
  userIsAdmin: boolean,
): Record<DeliveryType, boolean> {
  const availableDeliveryOptions: Record<DeliveryType, boolean> = {
    warehouse: true,
    savedDeliveryLocation: false,
    customerLocation: false,
  };

  if (
    repurchaseRequest.type !== "cask" &&
    (userIsAdmin || storageLocationView.availableForShipping)
  ) {
    if (organisationDeliveryLocations.length > 0) {
      availableDeliveryOptions.savedDeliveryLocation = true;
    }
    availableDeliveryOptions.customerLocation = true;
  }
  return availableDeliveryOptions;
}
